/**
 * Date utilities for formatting and parsing.
 * IMPORTANT: Date_getYYYYMMDD, Date_get24hHHMM, Date_formatVerbose, etc. use the
 * runtime's local timezone (getFullYear(), getMonth(), getHours(), ...).
 * Only use these in CLIENT components. Using them in server components or during
 * SSR causes hydration mismatch (server timezone ≠ client timezone) and can lead
 * to "removeChild" / DOM errors. For match/season date display, prefer
 * <MatchDateDisplay date={...} variant="date" | "datetime" /> in client components.
 */
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

const pad = (number: number) => {
    if (number < 10) return '0' + number
    return number.toString()
}

export const Date_getLocal = (date: Date) => {
    // Ensure we have a valid Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.error('[Date_getLocal] Invalid date:', date)
        return new Date()
    }

    // Use the Date object's methods directly instead of parsing toString()
    // This is more reliable across different locales and timezones
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    const hour = date.getHours()
    const minute = date.getMinutes()
    const second = date.getSeconds()

    // Create a new Date in UTC with the local date/time components
    const local_date = new Date(Date.UTC(year, month, day, hour, minute, second))
    return local_date
}

export const Date_getYYYYMMDDFromLocal = (local_date: Date) => {
    const year = local_date.getFullYear()
    const month = pad(local_date.getMonth() + 1)
    const day = pad(local_date.getDate())
    return `${year}/${month}/${day}`
}

/** Formats date as YYYY/MM/DD using the runtime's local timezone. Accepts Date or ISO string. */
export const Date_getYYYYMMDD = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    if (!(d instanceof Date) || isNaN(d.getTime())) return '--'
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`
}

/**
 * Returns one week key per calendar week that contains at least one match.
 * Each key is the start-of-week date (YYYY/MM/DD) so filtering with dayjs startOf('week') matches.
 * Uses UTC date part when available; falls back to local for compatibility.
 */
export const Date_getWeeksFromDateArray = (matchDates: Date[]) => {
    if (!matchDates?.length) return []
    const validDates = matchDates.filter(d => d instanceof Date && !isNaN(d.getTime()))
    if (validDates.length === 0) return []
    const weekStarts = new Set<string>()
    const useUtc = typeof dayjs.utc === 'function'
    for (const d of validDates) {
        let startOfWeek: dayjs.Dayjs
        if (useUtc) {
            const iso = d.toISOString()
            const datePart = iso.split('T')[0]
            if (!datePart || datePart === 'Invalid') continue
            const parsed = dayjs.utc(datePart)
            if (!parsed.isValid()) continue
            startOfWeek = parsed.startOf('week')
        } else {
            const local = Date_getLocal(d)
            startOfWeek = dayjs(local).startOf('week')
        }
        const key = `${startOfWeek.year()}/${pad(startOfWeek.month() + 1)}/${pad(startOfWeek.date())}`
        if (!key.includes('NaN')) weekStarts.add(key)
    }
    return Array.from(weekStarts).sort((a, b) => {
        const [yA, mA, dA] = a.split('/').map(Number)
        const [yB, mB, dB] = b.split('/').map(Number)
        if (yA !== yB) return yA - yB
        if (mA !== mB) return mA - mB
        return dA - dB
    })
}

/** Formats time as HH:mm (24h) using the runtime's local timezone. Accepts Date or ISO string. */
export const Date_get24hHHMM = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    if (!(d instanceof Date) || isNaN(d.getTime())) return '--:--'
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const Date_toYMDString = (date: Date): string => {
    const local_date = Date_getLocal(date)
    const stringDate = local_date.getFullYear() + '/' + (local_date.getMonth() + 1) + '/' + local_date.getDate()
    return stringDate
}

export const Date_getDaysOfWeekFull = () => {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
}

export const Date_getTime = (date: Date): number => {
    const local_date = Date_getLocal(date)
    return local_date.getTime()
}

export const Date_areWithinMinutesInterval = (date1: Date, date2: Date, minutes: number) => {
    const timeDifference = Math.abs(date1.getTime() - date2.getTime())
    return timeDifference < minutes * 60000 ? true : false
}

export const Date_isWithin24Hours = (date: Date): boolean => {
    const local_date = Date_getLocal(date)
    const currentTimestamp = Date.now()
    const dateToCheckTimestamp = local_date.getTime()
    const timeDifference = currentTimestamp - dateToCheckTimestamp
    return Math.abs(timeDifference) <= 24 * 60 * 60 * 1000
}

export const Date_formDataDateAndTime = (date: string) => {
    const parsedDate = dayjs(date.toString(), 'MM/DD/YYYY hh:mm A')
    return parsedDate.toDate()
}

export const Date_formatVerbose = (date: Date | string): string => {
    try {
        if (!date) {
            console.error('Date_formatVerbose: Received null or undefined date')
            return 'Invalid date'
        }

        // Handle both Date objects and string inputs
        const dayjsDate = typeof date === 'string' ? dayjs(date) : dayjs(date)

        if (!dayjsDate.isValid()) {
            console.error('Date_formatVerbose: Invalid date format received:', date)
            return 'Invalid date'
        }

        const day = dayjsDate.date()
        const suffix =
            ['th', 'st', 'nd', 'rd'][(day % 10 > 3 ? 0 : day % 10) - ((day % 100) - (day % 10) == 10 ? 0 : 1)] || 'th'
        return dayjsDate.format(`D[${suffix}] MMMM YYYY`)
    } catch (error) {
        console.error('Date_formatVerbose: Error formatting date:', error, 'Input:', date)
        return 'Invalid date'
    }
}

/** Formats date as "Jan 20, 2025". Accepts Date, ISO string, or DD/MM/YYYY. */
export const Date_formatShort = (date: Date | string): string => {
    if (date === null || date === undefined) return '—'
    let d: Date
    if (typeof date === 'string') {
        const trimmed = date.trim()
        const iso = new Date(trimmed)
        if (!isNaN(iso.getTime())) {
            d = iso
        } else {
            const parsed = dayjs(trimmed, 'DD/MM/YYYY', true)
            d = parsed.isValid() ? parsed.toDate() : new Date(trimmed)
        }
    } else {
        d = date
    }
    if (!(d instanceof Date) || isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
